/**
 * SF-T-302: how a build run (Implement task, Follow up) gets to its pull request, the flows' `pause` input.
 *
 * - `suggestions first`: the agent builds the whole task and pushes, without a PR. When its report has
 *   suggestions or questions, the run waits for people's picks; once nothing is left to decide (or someone
 *   presses Wrap up) it opens the PR, and the checks and review follow.
 * - `straight to PR`: the agent opens the PR in its first turn and the checks and review follow (the old default).
 * - `live` (SF-T-209): the agent does a first slice with its dev servers running and waits after every turn
 *   until Wrap up.
 *
 * Both pausing modes are a live run in the database (`run.live`) and run the flows' agent.t3.live step.
 */
export const RUN_PAUSE_INPUT = 'pause';

export const RUN_PAUSES = ['suggestions first', 'straight to PR', 'live'] as const;
export type RunPause = (typeof RUN_PAUSES)[number];

export const RUN_PAUSE_DEFAULT: RunPause = 'suggestions first';

export function isRunPause(value: unknown): value is RunPause {
  return typeof value === 'string' && (RUN_PAUSES as readonly string[]).includes(value);
}

/** A pause that holds the PR back: the run is live, and Wrap up (or, for suggestions first, nothing left to decide) sends it on. */
export function pausesBeforePr(pause: RunPause | null | undefined): boolean {
  return pause === 'suggestions first' || pause === 'live';
}

/** The flows with the choice (Implement task, Follow up): their runs from before SF-T-302 went straight to the PR unless live. */
export const RUN_PAUSE_FLOW_IDS: readonly string[] = ['implement-task', 'follow-up'];

/**
 * A run's pause from its row: whether it ran live, its `pause` input and its flow. A live run from before SF-T-302 had
 * a Live switch and no pause: `live`. A run that wasn't live is `straight to PR` when it said so, or when it's a run of
 * a flow with the choice from before SF-T-302 (that's what those did); else null (a flow without the choice).
 */
export function runPauseOf(live: boolean, input: unknown, flowId?: string | null): RunPause | null {
  if (live) return input === 'suggestions first' ? 'suggestions first' : 'live';
  if (input === 'straight to PR') return 'straight to PR';
  return input == null && flowId && RUN_PAUSE_FLOW_IDS.includes(flowId) ? 'straight to PR' : null;
}
